import {
  BanknoteIcon,
  CalendarIcon,
  CheckCircle2Icon,
  ExternalLinkIcon,
  HashIcon,
  MailIcon,
  PhoneIcon,
  UserIcon,
  WalletIcon,
} from 'lucide-react';

import { Badge } from '@/components/badge/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/dialog/dialog';

import type { BaseDialogProps } from '@/contexts/dialog-context/v2-base-dialog-props';
import { useV2DialogContext } from '@/contexts/dialog-context/v2-dialog-context';
import type { WithdrawalDTO } from '@/features/withdraws/schemas/get-withdrawals.schema';

const dateFormatter = new Intl.DateTimeFormat('id-ID', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

function formatDate(iso: string | null | undefined) {
  if (!iso) return '-';
  return dateFormatter.format(new Date(iso));
}

function formatRupiah(amount: number) {
  return `Rp ${amount.toLocaleString('id-ID')}`;
}

function statusBadgeVariant(status: string | null | undefined) {
  if (!status) return 'neutral' as const;
  const normalized = status.toLowerCase();
  if (normalized === 'completed' || normalized === 'success') {
    return 'success' as const;
  }
  if (
    normalized === 'request' ||
    normalized === 'pending' ||
    normalized === 'processing'
  ) {
    return 'warning' as const;
  }
  if (normalized === 'rejected' || normalized === 'cancel') {
    return 'destructive' as const;
  }
  return 'info' as const;
}

export interface DetailWithdrawDialogProps extends BaseDialogProps {
  withdrawal: WithdrawalDTO;
}

export default function DetailWithdrawDialog({
  dialog,
  withdrawal,
}: DetailWithdrawDialogProps) {
  const { closeDetailWithdrawDialog } = useV2DialogContext();

  return (
    <Dialog
      {...dialog}
      onOpenChange={nextOpenState => {
        if (!nextOpenState) {
          closeDetailWithdrawDialog();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Detail Penarikan</DialogTitle>
          <DialogDescription>
            Informasi lengkap tentang penarikan saldo mentor ini.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-6">
          <DetailWithdrawHeader withdrawal={withdrawal} />
          <DetailWithdrawMentor withdrawal={withdrawal} />
          <DetailWithdrawBank withdrawal={withdrawal} />
          <DetailWithdrawProof withdrawal={withdrawal} />
          <DetailWithdrawMeta withdrawal={withdrawal} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface DetailWithdrawSectionProps {
  withdrawal: WithdrawalDTO;
}

function DetailWithdrawHeader({ withdrawal }: DetailWithdrawSectionProps) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex flex-col gap-1.5">
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <HashIcon className="size-3" />
          ID #{withdrawal.idWithdrawal}
        </p>
        <p className="text-2xl font-semibold text-foreground">
          {formatRupiah(withdrawal.amount)}
        </p>
      </div>
      <Badge variant={statusBadgeVariant(withdrawal.status)}>
        {withdrawal.status}
      </Badge>
    </div>
  );
}

function DetailWithdrawMentor({ withdrawal }: DetailWithdrawSectionProps) {
  const mentor = withdrawal.mentor;

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border bg-muted/30 p-4">
      <p className="text-xs font-medium text-muted-foreground">Mentor</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <InfoRow
          icon={<UserIcon className="size-4" />}
          label="Nama"
          value={mentor?.fullName ?? '-'}
        />
        <InfoRow
          icon={<MailIcon className="size-4" />}
          label="Email"
          value={mentor?.email ?? '-'}
        />
        <InfoRow
          icon={<PhoneIcon className="size-4" />}
          label="Telepon"
          value={mentor?.phoneNumber ?? '-'}
        />
      </div>
    </div>
  );
}

function DetailWithdrawBank({ withdrawal }: DetailWithdrawSectionProps) {
  const mentor = withdrawal.mentor;

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border bg-muted/30 p-4">
      <p className="text-xs font-medium text-muted-foreground">Rekening</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <InfoRow
          icon={<BanknoteIcon className="size-4" />}
          label="Bank"
          value={mentor?.bankName ?? '-'}
        />
        <InfoRow
          icon={<WalletIcon className="size-4" />}
          label="Nomor Rekening"
          value={mentor?.bankAccountNumber ?? '-'}
        />
        <InfoRow
          icon={<UserIcon className="size-4" />}
          label="Atas Nama"
          value={mentor?.bankAccountHolderName ?? '-'}
        />
      </div>
    </div>
  );
}

function DetailWithdrawProof({ withdrawal }: DetailWithdrawSectionProps) {
  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs font-medium text-muted-foreground">
        Bukti Transfer
      </p>
      {withdrawal.transferProof ? (
        <a
          href={withdrawal.transferProof}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex w-fit items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1 text-xs text-foreground transition-colors hover:bg-muted"
        >
          <ExternalLinkIcon className="size-3.5" />
          Lihat bukti transfer
        </a>
      ) : (
        <p className="text-sm text-muted-foreground">Belum diunggah</p>
      )}
    </div>
  );
}

function DetailWithdrawMeta({ withdrawal }: DetailWithdrawSectionProps) {
  return (
    <div className="flex flex-col gap-2 border-t border-border pt-4">
      <InfoRow
        icon={<CalendarIcon className="size-4" />}
        label="Diajukan Pada"
        value={formatDate(withdrawal.requestedAt)}
      />
      <InfoRow
        icon={<CheckCircle2Icon className="size-4" />}
        label="Diselesaikan Pada"
        value={formatDate(withdrawal.completedAt)}
      />
      <InfoRow
        icon={<CalendarIcon className="size-4" />}
        label="Dibuat Pada"
        value={formatDate(withdrawal.createdAt)}
      />
      <InfoRow
        icon={<CalendarIcon className="size-4" />}
        label="Diperbarui Pada"
        value={formatDate(withdrawal.updatedAt)}
      />
    </div>
  );
}

interface InfoRowProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

function InfoRow({ icon, label, value }: InfoRowProps) {
  return (
    <div className="flex items-start gap-2.5">
      <span className="mt-0.5 text-muted-foreground">{icon}</span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="wrap-break-word text-sm font-medium text-foreground">
          {value}
        </p>
      </div>
    </div>
  );
}
